import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Database, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Zap, 
  Settings, 
  Play, 
  Pause,
  RotateCcw,
  Target,
  Award,
  Activity,
  Globe,
  BarChart3,
  Cpu,
  Brain,
  Workflow,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const NeuralNode = React.memo(({ node, isActive, onClick }) => {
  const icon = useMemo(() => {
    if (node.type === 'data') return <Database size={20} className="text-blue-300" />;
    if (node.type === 'process') return <Cpu size={20} className="text-purple-300" />;
    return <Target size={20} className="text-green-300" />;
  }, [node.type]);

  return (
    <motion.div
      className={`absolute w-14 h-14 rounded-full border-2 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ${
        node.type === 'data' 
          ? 'bg-blue-500/20 border-blue-400/50 hover:bg-blue-500/40' 
          : node.type === 'process' 
            ? 'bg-purple-500/20 border-purple-400/50 hover:bg-purple-500/40' 
            : 'bg-green-500/20 border-green-400/50 hover:bg-green-500/40'
      } ${isActive ? 'ring-2 ring-white/50 scale-110' : ''}`}
      style={{
        left: `${node.x}%`,
        top: `${node.y}%`,
        transform: 'translate(-50%, -50%)'
      }}
      animate={{
        scale: [1, 1.05, 1],
        boxShadow: [
          `0 0 0px rgba(0,0,0,0)`,
          `0 0 15px ${node.type === 'data' ? 'rgba(59, 130, 246, 0.4)' : node.type === 'process' ? 'rgba(168, 85, 247, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`,
          `0 0 0px rgba(0,0,0,0)`
        ]
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      whileHover={{ scale: 1.15 }}
      onClick={onClick}
    >
      {icon}
      <div className="mt-1 text-white text-xs font-bold">
        ${Math.max(0, Math.floor(node.value)).toLocaleString()}
      </div>
    </motion.div>
  );
});

const ConnectionLine = React.memo(({ from, to }) => {
  if (!from || !to) return null;
  
  return (
    <motion.line
      x1={`${from.x}%`}
      y1={`${from.y}%`}
      x2={`${to.x}%`}
      y2={`${to.y}%`}
      stroke="url(#connectionGradient)"
      strokeWidth="1.5"
      opacity={0.7}
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
    />
  );
});

const App = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [metrics, setMetrics] = useState({
    revenue: 0,
    expenses: 0,
    profit: 0,
    efficiency: 85,
    dataPoints: 0,
    connections: 0,
    uptime: 0
  });
  const [nodes, setNodes] = useState([]);
  const [activeNode, setActiveNode] = useState(null);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  // Initialize neural network
  useEffect(() => {
    const initialNodes = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: Math.random() * 75 + 12.5,
      y: Math.random() * 75 + 12.5,
      type: ['data', 'process', 'output'][Math.floor(Math.random() * 3)],
      value: Math.random() * 500 + 100,
      connections: []
    }));
    
    // Create initial connections
    const connectedNodes = initialNodes.map(node => {
      const connections = [];
      for (let i = 0; i < 2; i++) {
        const target = Math.floor(Math.random() * initialNodes.length);
        if (target !== node.id && !connections.includes(target)) {
          connections.push(target);
        }
      }
      return { ...node, connections };
    });
    
    setNodes(connectedNodes);
  }, []);

  // Simulation logic
  const simulateStep = useCallback(() => {
    setMetrics(prev => {
      const revenueInc = Math.random() * 120 + 40;
      const expenseInc = Math.random() * 25 + 8;
      const newRevenue = prev.revenue + revenueInc;
      const newExpenses = prev.expenses + expenseInc;
      const newProfit = newRevenue - newExpenses;
      const newEfficiency = Math.min(99.9, Math.max(70, prev.efficiency + (Math.random() * 1.5 - 0.7)));
      const newDataPoints = prev.dataPoints + Math.floor(Math.random() * 4) + 1;
      const newConnections = prev.connections + Math.floor(Math.random() * 1.5);
      const newUptime = prev.uptime + 1;

      return {
        revenue: newRevenue,
        expenses: newExpenses,
        profit: newProfit,
        efficiency: newEfficiency,
        dataPoints: newDataPoints,
        connections: newConnections,
        uptime: newUptime
      };
    });

    setNodes(prev => 
      prev.map(node => {
        const valueChange = Math.random() * 40 - 8;
        const newValue = Math.max(0, node.value + valueChange);
        
        // Occasionally add new connections
        let newConnections = [...node.connections];
        if (newConnections.length < 3 && Math.random() > 0.7) {
          const newTarget = Math.floor(Math.random() * prev.length);
          if (newTarget !== node.id && !newConnections.includes(newTarget)) {
            newConnections.push(newTarget);
          }
        }
        
        return { ...node, value: newValue, connections: newConnections };
      })
    );
  }, []);

  // Main simulation effect
  useEffect(() => {
    if (isRunning) {
      startTimeRef.current = Date.now();
      intervalRef.current = setInterval(simulateStep, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning, simulateStep]);

  // Reset system
  const resetSystem = useCallback(() => {
    setIsRunning(false);
    setMetrics({
      revenue: 0,
      expenses: 0,
      profit: 0,
      efficiency: 85,
      dataPoints: 0,
      connections: 0,
      uptime: 0
    });
    setActiveNode(null);
    
    // Reset nodes with new positions
    const resetNodes = nodes.map(node => ({
      ...node,
      x: Math.random() * 75 + 12.5,
      y: Math.random() * 75 + 12.5,
      value: Math.random() * 500 + 100
    }));
    setNodes(resetNodes);
  }, [nodes]);

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${Math.floor(num)}`;
  };

  // Calculate projected profits
  const projections = useMemo(() => {
    const { profit } = metrics;
    return {
      hour: profit * 0.12,
      day: profit * 2.8,
      week: profit * 19.6,
      month: profit * 84
    };
  }, [metrics.profit]);

  // Format uptime
  const formatUptime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 text-white overflow-hidden relative">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(120,119,198,0.1),transparent_50%)]"></div>
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            animate={{
              x: [0, Math.random() * 100 - 50, 0],
              y: [0, Math.random() * 100 - 50, 0],
              opacity: [0, Math.random() * 0.7 + 0.3, 0]
            }}
            transition={{
              duration: Math.random() * 15 + 10,
              repeat: Infinity,
              delay: Math.random() * 5
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-500/20 rounded-lg border border-purple-500/30">
              <Brain className="w-6 h-6 text-purple-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Neural Profit Cortex
            </h1>
            <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              isRunning ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            }`}>
              {isRunning ? <CheckCircle size={14} className="mr-1" /> : <AlertTriangle size={14} className="mr-1" />}
              {isRunning ? 'Operational' : 'Standby'}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3 justify-end">
            <button
              onClick={() => setIsRunning(!isRunning)}
              className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-semibold transition-all shadow-lg ${
                isRunning 
                  ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-red-500/20' 
                  : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-emerald-500/20'
              }`}
            >
              {isRunning ? <Pause size={18} /> : <Play size={18} />}
              <span className="font-medium">{isRunning ? 'Pause' : 'Activate'}</span>
            </button>
            <button
              onClick={resetSystem}
              className="flex items-center space-x-2 px-4 py-2.5 bg-gray-800/60 hover:bg-gray-700/60 backdrop-blur-sm rounded-xl font-semibold transition-all border border-gray-700/50"
            >
              <RotateCcw size={18} />
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Profit', value: formatNumber(metrics.profit), icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { label: 'Revenue', value: formatNumber(metrics.revenue), icon: DollarSign, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            { label: 'Efficiency', value: `${metrics.efficiency.toFixed(1)}%`, icon: Zap, color: 'text-amber-400', bg: 'bg-amber-500/10' },
            { label: 'Data Points', value: metrics.dataPoints.toLocaleString(), icon: Database, color: 'text-violet-400', bg: 'bg-violet-500/10' },
            { label: 'Connections', value: metrics.connections.toLocaleString(), icon: Workflow, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
            { label: 'Uptime', value: formatUptime(metrics.uptime), icon: Activity, color: isRunning ? 'text-green-400' : 'text-gray-400', bg: 'bg-gray-800/50' }
          ].map((metric, i) => (
            <motion.div
              key={i}
              className={`${metric.bg} backdrop-blur-sm rounded-xl p-4 border border-gray-700/50 flex flex-col`}
              whileHover={{ y: -3 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="flex items-center space-x-2 mb-2">
                <metric.icon className={`w-4 h-4 ${metric.color}`} />
                <span className="text-xs text-gray-300 font-medium">{metric.label}</span>
              </div>
              <div className={`text-lg font-bold ${metric.color}`}>
                {metric.value}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Neural Network Visualization */}
        <div className="relative bg-gray-800/30 backdrop-blur-xl rounded-2xl p-5 border border-gray-700/50 mb-6 h-[400px] overflow-hidden shadow-xl">
          <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
            <div className="p-1.5 bg-purple-500/20 rounded-lg">
              <Brain className="w-5 h-5 text-purple-400" />
            </div>
            <span>Autonomous Neural Network</span>
            <span className="text-sm text-gray-400 ml-2">({nodes.length} active nodes)</span>
          </h2>
          
          {/* Connection Lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            <defs>
              <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.7" />
              </linearGradient>
            </defs>
            {nodes.flatMap((node, i) => 
              node.connections.map(targetIndex => (
                <ConnectionLine 
                  key={`${i}-${targetIndex}`} 
                  from={node} 
                  to={nodes[targetIndex]} 
                />
              ))
            )}
          </svg>

          {/* Neural Nodes */}
          {nodes.map((node) => (
            <NeuralNode 
              key={node.id} 
              node={node} 
              isActive={activeNode === node.id}
              onClick={() => setActiveNode(node.id === activeNode ? null : node.id)}
            />
          ))}
        </div>

        {/* Performance Metrics & Projections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance Metrics */}
          <div className="bg-gray-800/40 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 shadow-xl">
            <h3 className="text-lg font-bold mb-5 flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              <span>System Performance</span>
            </h3>
            <div className="space-y-5">
              {[
                {
                  label: 'Revenue Growth',
                  value: Math.min(100, (metrics.revenue / 15000) * 100),
                  color: 'bg-emerald-500',
                  metric: `+${(metrics.revenue * 0.018).toFixed(1)}%`
                },
                {
                  label: 'Cost Efficiency',
                  value: Math.min(100, 100 - (metrics.expenses / 8000) * 100),
                  color: 'bg-amber-500',
                  metric: `-${(metrics.expenses * 0.008).toFixed(1)}%`
                },
                {
                  label: 'Network Efficiency',
                  value: metrics.efficiency,
                  color: 'bg-violet-500',
                  metric: `${metrics.efficiency.toFixed(1)}%`
                }
              ].map((item, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-300">{item.label}</span>
                    <span className={`font-semibold ${item.color.replace('bg-', 'text-')}`}>
                      {item.metric}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700/50 rounded-full h-2.5 overflow-hidden">
                    <motion.div
                      className={`h-full ${item.color} rounded-full`}
                      initial={{ width: 0 }}
                      animate={{ width: `${item.value}%` }}
                      transition={{ duration: 1.2, ease: "easeOut" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Profit Projections */}
          <div className="bg-gray-800/40 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 shadow-xl">
            <h3 className="text-lg font-bold mb-5 flex items-center space-x-2">
              <Target className="w-5 h-5 text-emerald-400" />
              <span>Profit Projections</span>
            </h3>
            <div className="space-y-4">
              {[
                { period: 'Next Hour', value: projections.hour },
                { period: 'Next Day', value: projections.day },
                { period: 'Next Week', value: projections.week },
                { period: 'Next Month', value: projections.month }
              ].map((item, i) => (
                <motion.div 
                  key={i}
                  className="flex justify-between items-center py-2 border-b border-gray-700/30"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <span className="text-gray-300">{item.period}</span>
                  <span className="text-emerald-400 font-bold text-lg">
                    +{formatNumber(item.value)}
                  </span>
                </motion.div>
              ))}
              
              <div className="mt-6 p-4 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-xl border border-emerald-500/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Award className="text-amber-400" size={18} />
                  <span className="font-medium text-amber-400">Optimization Tip</span>
                </div>
                <p className="text-sm text-gray-300">
                  Current efficiency at {metrics.efficiency.toFixed(1)}%. 
                  {metrics.efficiency > 90 ? ' Excellent performance!' : metrics.efficiency > 80 ? ' Good performance.' : ' Consider optimization.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>Neural Profit Cortex v2.1 • Autonomous Financial Intelligence System</p>
          <p className="mt-1">Cloudflare Optimized • Real-time Simulation</p>
        </div>
      </div>
    </div>
  );
};

export default App;
